import { type EligibleProduct, useIntegratedFlowStore, useProductOfferStore } from "@expert/sales-tools";
import { Button, Flex, Text } from "@mantine/core";
import { type JSX, useState } from "react";

import type { IntegratedSalesSteps } from "@/sales-tools/home-product/components/types";

import { useAnalytics, withPolymorphicClickAnalytics } from "@/analytics";
import { isSimulatedVoiceTask, useLogger, usePartner, useSessionId, useTypedMostRecentTask } from "@/sdk";

import { postDeclination } from "../../api";
import { SaleProgress } from "../../types";
import { RightArrowIcon } from "../Icons";
import styles from "./SalesFooter.module.css";

interface ContinueButtonProps {
    text?: string;
    onClick?: () => Promise<void>;
    nextStep?: IntegratedSalesSteps;
    isDisabled?: boolean;
}

interface SalesFooterProps {
    product?: EligibleProduct;
    onBack?: () => void;
    continueButtonProps?: ContinueButtonProps;
    buttonSlot?: JSX.Element;
}

function getNextStep(product?: EligibleProduct): IntegratedSalesSteps {
    if (product?.isPrimaryProduct) {
        return product?.isProductAsurionBilled ? "CollectCustomerInfoStep" : "SendLinkStep";
    } else {
        return "InlineSaleTrackerStep";
    }
}

export function SalesFooter({
    product,
    onBack,
    continueButtonProps: { text = "Continue", nextStep = getNextStep(product), isDisabled = false, onClick } = {},
    buttonSlot,
}: SalesFooterProps): JSX.Element {
    const logger = useLogger({ module: "SalesFooter", supportTeam: "sales" });
    const AnalyticsButton = withPolymorphicClickAnalytics(Button<"button">, "ButtonClick");
    const [declining, setDeclining] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { isVoice, isChat, task } = useTypedMostRecentTask();

    if (!isVoice && !isChat) {
        throw new Error("Active task is not voice or chat task");
    }

    const sessionId = useSessionId();
    const updateProduct = useProductOfferStore((state) => state.updateProduct);
    const updateSelectedProduct = useProductOfferStore((state) => state.updateSelectedProduct);
    const integratedStep = useIntegratedFlowStore((state) => state.getCurrentStep(sessionId));
    const updateIntegratedStep = useIntegratedFlowStore((state) => state.updateIntegratedStep);

    const { dispatcher } = useAnalytics();
    const partner = usePartner();

    // In training mode, we don't want to allow the user to proceed with enrollment past
    // the initial eligibility step, so we check for isSimulatedVoiceTask to disable those buttons
    const isExpertUniversitySimulatedVoiceTask = isSimulatedVoiceTask(task);

    const decline = async () => {
        try {
            if (product) {
                await postDeclination(logger, {
                    sessionId,
                    partner,
                    productOfferId: product.id,
                    productSku: product.productSku,
                });
                updateProduct(sessionId, product.id, { saleProgress: SaleProgress.Declined });
            }
            setDeclining(false);
        } catch {
            setDeclining(false);
        }
    };

    const onContinue = async () => {
        void dispatcher
            .withExtra({ nextStep: integratedStep })
            .dispatchBusinessEvent("ButtonClicked_ContinueToDisclosures");
        if (onClick) {
            setIsLoading(true);
            try {
                await onClick?.();
            } catch {
                /* do nothing */
            } finally {
                setIsLoading(false);
            }
        } else {
            updateSelectedProduct(sessionId, product);
            updateIntegratedStep(sessionId, nextStep);
        }
    };

    return (
        <Flex className={styles.footerBox}>
            <Flex align={"center"} gap={"xs"}>
                {onBack && (
                    <Button
                        onClick={onBack}
                        size="xs"
                        variant="transparent"
                        color="dark.1"
                        fz="sm"
                        leftSection={<RightArrowIcon height="12" width="12" x="-1" />}
                    >
                        Back
                    </Button>
                )}

                {buttonSlot}
            </Flex>

            <Flex flex={1} gap={"xs"}>
                <AnalyticsButton
                    flex={1}
                    analytics={{
                        actionId: "EnrollmentDeclineButton",
                        eventData: {
                            nextStep: integratedStep,
                        },
                    }}
                    color="white"
                    data-testid="enrollment-decline-button"
                    size="sm"
                    loading={declining}
                    disabled={
                        !product ||
                        product.saleProgress !== SaleProgress.InProgress ||
                        isExpertUniversitySimulatedVoiceTask
                    }
                    onClick={() => {
                        setDeclining(true);
                        void decline();
                    }}
                    variant="outline"
                >
                    <Text td="underline" size="xs">
                        Decline
                    </Text>
                </AnalyticsButton>

                <Button
                    flex={2}
                    disabled={declining || !product || isDisabled || isExpertUniversitySimulatedVoiceTask}
                    className={styles.actionText}
                    color={isChat ? "primary.6" : "primary.5"}
                    fz="xs"
                    size="sm"
                    data-testid="continue-to-disclosures"
                    loading={isLoading}
                    onClick={() => void onContinue()}
                >
                    <Text fz="xs" c={isDisabled ? "var(--mantine-color-dark-3)" : "var(--mantine-color-white)"}>
                        {text}
                    </Text>
                </Button>
            </Flex>
        </Flex>
    );
}
